import { ControlData } from '@/services/soaUpdatesService';
import { AssuranceTask } from '@/services/assuranceActionsService';

/**
 * Source types for items that can appear in the unified table
 */
export enum TableItemSource {
  AI_SUGGESTION = 'ai_suggestion',
  SOA_CONTROL = 'soa_control'
}

/**
 * Interfaces for AI-suggested tasks document (Firestore shape)
 */
export interface AISuggestedTaskItem {
  cast_id?: string;
  priority?: 'low' | 'medium' | 'high' | string;
  confidence?: number;
  task_description?: string;
  evidence_description?: string;
  iso_clauses?: string[] | string;
  rationale?: string;
  status?: 'accepted' | 'rejected' | 'pending';
}

export interface AITaskComparison {
  action_recommended?: string;
  similarity_score?: number;
  suggested_task_id?: string;
  existing_task_id?: string;
  comparison_reason?: string;
  existing_task_description?: string;
  suggested_task_description?: string;
}

// Firestore AI suggestions document (grouped suggestions per source document)
export interface SuggestedAssuranceTask {
  id: string; // Firestore doc ID
  document_id?: string;
  file_name?: string;
  document_reference?: string;
  status?: string;
  summary?: string;
  suggested_tasks?: AISuggestedTaskItem[];
  task_comparisons?: AITaskComparison[];
  createdAt?: any; // Timestamp
}

/**
 * Enum for different types of update cells
 */
export enum UpdateCellType {
  SOA_CONTROL = 'soa_control',
  ASSURANCE_TASK = 'assurance_task',
  SUGGESTED_ASSURANCE_TASK = 'suggested_assurance_task',
  TASK_CENTRIC_ASSURANCE_TASK = 'task_centric_assurance_task',
  TABLE_TASK = 'table_task',
  // Add more cell types as needed
}

/**
 * Base interface for all update cells
 */
export interface UpdateCellData {
  id: string;
  type: UpdateCellType;
  data: unknown; // Type will depend on the cell type
}

/**
 * Specific interface for SOA control cells
 */
export interface SOAControlCellData extends UpdateCellData {
  type: UpdateCellType.SOA_CONTROL;
  data: ControlData;
}

/**
 * Specific interface for Assurance Task cells
 */
export interface AssuranceTaskCellData extends UpdateCellData {
  type: UpdateCellType.ASSURANCE_TASK;
  data: AssuranceTask;
}

/**
 * Specific interface for Suggested Assurance Task cells
 */
export interface SuggestedAssuranceTaskCellData extends UpdateCellData {
  type: UpdateCellType.SUGGESTED_ASSURANCE_TASK;
  data: SuggestedAssuranceTask;
}

/**
 * Specific interface for Task-Centric Assurance Task cells
 */
export interface TaskCentricAssuranceTaskCellData extends UpdateCellData {
  type: UpdateCellType.TASK_CENTRIC_ASSURANCE_TASK;
  data: SuggestedAssuranceTask;
}

/**
 * Specific interface for Table Task cells (table view with bulk actions)
 */
export interface TableTaskCellData extends UpdateCellData {
  type: UpdateCellType.TABLE_TASK;
  data: {
    suggestions: SuggestedAssuranceTask[];
  };
}

/**
 * Props interface for cell components
 */
export interface UpdateCellProps<T = unknown> {
  cellData: UpdateCellData & { data: T };
  activeTab: string;
  onTabChange: (id: string, tabValue: string) => void;
}

/**
 * Common interface for normalized table items that can represent both AI suggestions and SOA controls
 */
export interface NormalizedTableItem {
  // Common fields
  id: string; // Unique ID for the item
  itemType: TableItemSource; // Source type (AI or SOA)
  sourceId: string; // Original ID from source (suggestionDocId or controlID)
  originalId: string; // Original item ID within source
  description?: string; // Main description text
  
  // AI suggestion specific
  priority?: 'low' | 'medium' | 'high' | string;
  confidence?: number;
  evidence?: string;
  isoClauses?: string[];
  rationale?: string;
  documentName?: string;
  
  // SOA control specific
  status?: string;
  implementationDetails?: string;
  controlNumber?: string;
  
  // Additional properties can be attached as needed
  [key: string]: any;
}

/**
 * Interface for unified table cell data
 */
export interface UnifiedTableCellData extends UpdateCellData {
  type: UpdateCellType.TABLE_TASK;
  data: {
    items: NormalizedTableItem[];
    aiSuggestions?: SuggestedAssuranceTask[];
    soaControls?: ControlData[];
  };
}
